import {
  usePlatformNotificationStore,
  type PlatformNotificationView,
} from '@/platform-notifications/platform-notification-store';
import { useIncidentStore } from '@/incidents/incident-store';
import { getTracker } from '@/telemetry/tracker';

/**
 * Platform-notification toaster — a QUEUE of dismissible toasts pinned
 * bottom-left (parity with the incident banner's placement). Each notification
 * is its own card so multiple notifications stack without overlapping; toasts
 * persist until the user closes them.
 *
 * Driven by the platform-notification store, fed by the SignalR
 * `portalnotificationmethod` hub (see `platform-notifications-bridge.ts`).
 *
 * Both this and the incident banner pin bottom-left; when the banner is shown we
 * lift the stack above it so the two toasters never collide.
 */

type PlatformNotificationType = PlatformNotificationView['type'];

/** Category label shown as the leading badge on each toast. */
const TYPE_LABEL: Record<PlatformNotificationType, string> = {
  action: 'Action required',
  info: 'Info',
  warning: 'Warning',
  error: 'Error',
};

function PlatformNotificationCard({
  notification,
  onDismiss,
}: {
  notification: PlatformNotificationView;
  onDismiss: () => void;
}) {
  const { id, type, title, message, source, action } = notification;
  // Errors/warnings interrupt (assertive); info/action announce politely.
  const assertive = type === 'error' || type === 'warning';

  const onActionClick = () => {
    getTracker().event('platform_notification_action_clicked', { id, type, source });
  };

  const onClose = () => {
    getTracker().event('platform_notification_dismissed', { id, type, source });
    onDismiss();
  };

  return (
    <article
      className={`platform-notification-toast platform-notification-toast--${type}`}
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
    >
      <div className="platform-notification-toast__body">
        <div className="platform-notification-toast__head">
          <span className="platform-notification-toast__badge">{TYPE_LABEL[type]}</span>
          {source ? (
            <span className="platform-notification-toast__source">Source: {source}</span>
          ) : null}
        </div>
        {title ? <strong className="platform-notification-toast__title">{title}</strong> : null}
        {message ? <p className="platform-notification-toast__message">{message}</p> : null}
        {action ? (
          <a
            className="platform-notification-toast__action"
            href={action.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onActionClick}
          >
            {action.label}
          </a>
        ) : null}
      </div>
      <button
        type="button"
        className="platform-notification-toast__close"
        aria-label="Dismiss notification"
        onClick={onClose}
      >
        &times;
      </button>
    </article>
  );
}

export function PlatformNotificationToaster() {
  const items = usePlatformNotificationStore((s) => s.items);
  const dismiss = usePlatformNotificationStore((s) => s.dismiss);
  // Mirror the incident banner's visibility guard so we only reserve space for
  // it (and avoid overlapping it) when it is actually on screen.
  const incidentVisible = useIncidentStore((s) => !s.dismissed && s.incidents.length > 0);

  if (items.length === 0) return null;

  return (
    <section
      className={`platform-notification-toaster${
        incidentVisible ? ' platform-notification-toaster--offset' : ''
      }`}
      aria-label="Notifications"
    >
      {items.map((notification) => (
        <PlatformNotificationCard
          key={notification.id}
          notification={notification}
          onDismiss={() => dismiss(notification.id)}
        />
      ))}
    </section>
  );
}
